<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Left Sidebar -->
    <Sidebar />

    <!-- Right Sidebar (only for doctor, not manager) -->
    <DoctorRightSidebar
      v-if="isDoctorMode"
      ref="rightSidebar"
      :appointments="rightSidebarAppointments"
      @appointment-click="onSidebarAppointmentClick"
    />

    <!-- Main content with margin for both sidebars -->
    <div
      class="transition-all duration-300"
      :style="mainContentStyle"
    >
      <!-- Page content -->
      <main class="p-6 w-full">
        <slot />
      </main>
    </div>
  </div>
</template>

<script>
import Sidebar from './Sidebar.vue'
import DoctorRightSidebar from './doctor/DoctorRightSidebar.vue'
import staffApi from '@/services/staffApi'
import sessionBootstrap from '@/state/sessionBootstrap'
import {
  LEFT_SIDEBAR_KEY,
  RIGHT_SIDEBAR_KEY,
  SIDEBAR_SYNC_EVENT
} from '@/utils/sidebarSync'

export default {
  name: 'DoctorLayout',
  components: {
    Sidebar,
    DoctorRightSidebar
  },
  data() {
    const savedCollapsed = localStorage.getItem(LEFT_SIDEBAR_KEY) === 'true'
    const rightCollapsed = localStorage.getItem(RIGHT_SIDEBAR_KEY) === 'true'
    return {
      sidebarCollapsed: savedCollapsed,
      rightSidebarCollapsed: rightCollapsed,
      isDoctorMode: false,
      rightSidebarAppointments: []
    }
  },
  computed: {
    sessionUserType() {
      return sessionBootstrap.userType
    },
    leftWidth() {
      return this.sidebarCollapsed ? 72 : 260
    },
    rightWidth() {
      if (!this.isDoctorMode) return 0
      return this.rightSidebarCollapsed ? 66 : 305
    },
    mainContentStyle() {
      return {
        marginLeft: `${this.leftWidth}px`,
        marginRight: `${this.rightWidth}px`
      }
    }
  },
  async created() {
    this.isDoctorMode = this.sessionUserType === 'doctor'

    if (this.isDoctorMode) {
      await this.loadUpcomingAppointments()
    }

    // Watch localStorage for left sidebar changes
    window.addEventListener('storage', this.onStorageChange)
    window.addEventListener(SIDEBAR_SYNC_EVENT, this.onSidebarSync)
  },
  beforeUnmount() {
    window.removeEventListener('storage', this.onStorageChange)
    window.removeEventListener(SIDEBAR_SYNC_EVENT, this.onSidebarSync)
    if (this._cssObserver) {
      this._cssObserver.disconnect()
    }
  },
  mounted() {
    // Sync from localStorage flags directly
    this.sidebarCollapsed = localStorage.getItem(LEFT_SIDEBAR_KEY) === 'true'
    this.rightSidebarCollapsed = localStorage.getItem(RIGHT_SIDEBAR_KEY) === 'true'

    // Observe <html> style attribute changes (for --sidebar-width CSS var)
    this._cssObserver = new MutationObserver(() => {
      const w = getComputedStyle(document.documentElement)
        .getPropertyValue('--sidebar-width').trim()
      if (w) {
        this.sidebarCollapsed = parseInt(w) <= 72
      }
    })
    this._cssObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style']
    })
  },
  methods: {
    onSidebarSync(event) {
      const { key, collapsed } = event.detail || {}
      if (key === LEFT_SIDEBAR_KEY && typeof collapsed === 'boolean') {
        this.sidebarCollapsed = collapsed
      }
      if (key === RIGHT_SIDEBAR_KEY && typeof collapsed === 'boolean') {
        this.rightSidebarCollapsed = collapsed
      }
    },
    onStorageChange(e) {
      if (e.key === LEFT_SIDEBAR_KEY) {
        this.sidebarCollapsed = e.newValue === 'true'
      }
      if (e.key === RIGHT_SIDEBAR_KEY) {
        this.rightSidebarCollapsed = e.newValue === 'true'
      }
    },
    onSidebarAppointmentClick(appt) {
      // Emit event so child views (DoctorSchedule) can handle it if needed
      this.$emit('appointment-selected', appt)
    },
    async loadUpcomingAppointments() {
      try {
        const response = await staffApi.getMyStaffAppointments()
        const data = response.data.results || response.data || []
        // Show only scheduled, sorted soonest first
        this.rightSidebarAppointments = data
          .filter(a => a.status === 'scheduled')
          .slice(0, 5)
      } catch (_) {
        this.rightSidebarAppointments = []
      }
    }
  }
}
</script>

<style scoped>
/* Responsive adjustments */
@media (max-width: 768px) {
  /* Hide right sidebar on mobile */
}
</style>
